import React, { useState, useRef, useCallback, useLayoutEffect, useEffect } from "react";

// Per-pill text: target ~50% of pill height (host ratio), shrink width-wise if needed.
const PLAYER_ANSWER_LINE_HEIGHT = 1.12;
const PLAYER_ANSWER_HEIGHT_RATIO = 0.5;
const PLAYER_ANSWER_MIN_FONT_PX = 4;

export function PlayerAnswerFitText({ text, style = {}, horizontalInset = 24 }) {
  const boxRef = useRef(null);
  const textRef = useRef(null);
  const [fontPx, setFontPx] = useState(null);

  const recalc = useCallback(() => {
    const box = boxRef.current;
    const span = textRef.current;
    if (!box || !span) return;

    const pill = box.closest(".player-answer-option") || box.parentElement;
    if (!pill) return;

    const pillH = pill.clientHeight;
    const pillW = pill.clientWidth;
    if (pillH < 8 || pillW < 8) return;

    const boxW = box.clientWidth;
    const maxW =
      boxW > 8 ? Math.max(8, boxW - 8) : Math.max(8, pillW - horizontalInset);
    const targetCapH = pillH * PLAYER_ANSWER_HEIGHT_RATIO;
    let start = Math.max(
      PLAYER_ANSWER_MIN_FONT_PX,
      Math.floor(targetCapH / PLAYER_ANSWER_LINE_HEIGHT)
    );

    let f = start;
    while (f >= PLAYER_ANSWER_MIN_FONT_PX) {
      span.style.fontSize = `${f}px`;
      span.style.lineHeight = String(PLAYER_ANSWER_LINE_HEIGHT);
      void span.offsetWidth;
      if (span.scrollWidth <= maxW && span.scrollHeight <= pillH) {
        setFontPx(f);
        return;
      }
      f -= 1;
    }
    setFontPx(PLAYER_ANSWER_MIN_FONT_PX);
  }, [text, horizontalInset]);

  useLayoutEffect(() => {
    recalc();
  }, [recalc]);

  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === "undefined") return;
    const pill = box.closest(".player-answer-option") || box.parentElement;
    if (!pill) return;
    const ro = new ResizeObserver(() => recalc());
    ro.observe(pill);
    ro.observe(box);
    return () => ro.disconnect();
  }, [recalc]);

  return (
    <span
      ref={boxRef}
      style={{
        flex: 1,
        alignSelf: "stretch",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 0,
        minHeight: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        ...style,
      }}
    >
      <span
        ref={textRef}
        style={{
          fontSize: fontPx != null ? `${fontPx}px` : undefined,
          whiteSpace: "nowrap",
          lineHeight: PLAYER_ANSWER_LINE_HEIGHT,
        }}
      >
        {text}
      </span>
    </span>
  );
}
